import React, { useEffect, useState } from 'react';
import { Table, Button, Tag, Alert, Card, Input, Select, Space, Modal, Typography, Row, Col, Statistic, Timeline, Descriptions, Empty } from 'antd';
import { ShoppingCartOutlined, SearchOutlined, EyeOutlined, CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import api from '../../../services/api';
import { getStatusColor, formatCurrency } from '../../../utils/ui';

function AdminBookings() {
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [viewingBooking, setViewingBooking] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);

    useEffect(() => {
        loadBookings();
    }, []);

    const loadBookings = async () => {
        try {
            setLoading(true);
            const res = await api.adminListBookings({ per_page: 100 });
            setBookings(res.data.data || []);
        } catch (e) {
            setError('Failed to load bookings');
        } finally {
            setLoading(false);
        }
    };

    const updateBookingStatus = async (bookingId, status) => {
        try {
            await api.updateBookingStatus(bookingId, status);
            loadBookings();
        } catch (e) {
            setError('Failed to update booking status');
        }
    };

    const viewBookingDetails = (booking) => {
        setViewingBooking(booking);
        setModalVisible(true);
    };

    const statusColor = (status) => getStatusColor(status);

    const filteredBookings = bookings.filter(booking => {
        const matchesSearch = booking.booking_number?.toLowerCase().includes(searchText.toLowerCase()) ||
                            booking.user?.name?.toLowerCase().includes(searchText.toLowerCase()) ||
                            booking.vendor?.business_name?.toLowerCase().includes(searchText.toLowerCase());
        const matchesStatus = !statusFilter || booking.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    const columns = [
        {
            title: 'Booking #',
            dataIndex: 'booking_number',
            key: 'number',
            render: (number) => (
                <Typography.Text code>{number}</Typography.Text>
            )
        },
        {
            title: 'Customer',
            key: 'customer',
            render: (_, booking) => (
                <div>
                    <div className="font-medium">{booking.user?.name}</div>
                    <div className="text-sm text-gray-500">{booking.user?.email}</div>
                </div>
            )
        },
        {
            title: 'Vendor',
            dataIndex: ['vendor', 'business_name'],
            key: 'vendor'
        },
        {
            title: 'Event Date',
            dataIndex: 'event_date',
            key: 'event_date'
        },
        {
            title: 'Guests',
            dataIndex: 'guest_count',
            key: 'guests'
        },
        {
            title: 'Amount',
            dataIndex: 'total_amount',
            key: 'amount',
            render: (amount) => formatCurrency(amount)
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag color={statusColor(status)}>
                    {status?.charAt(0).toUpperCase() + status?.slice(1)}
                </Tag>
            )
        },
        {
            title: 'Payment',
            key: 'payment',
            render: (_, booking) => (
                <Tag color={statusColor(booking.order?.payment_status === 'paid' ? 'success' : 'pending')}>
                    {booking.order?.payment_status || 'Pending'}
                </Tag>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, booking) => (
                <Space>
                    <Button 
                        size="small" 
                        icon={<EyeOutlined />}
                        onClick={() => viewBookingDetails(booking)}
                    >
                        View
                    </Button>
                    {booking.status === 'pending' && (
                        <>
                            <Button 
                                type="primary" 
                                size="small" 
                                icon={<CheckCircleOutlined />}
                                onClick={() => updateBookingStatus(booking.id, 'confirmed')}
                            >
                                Confirm
                            </Button>
                            <Button 
                                danger 
                                size="small" 
                                icon={<CloseCircleOutlined />}
                                onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                            >
                                Cancel
                            </Button>
                        </>
                    )}
                </Space>
            )
        }
    ];

    const stats = {
        totalBookings: bookings.length,
        pendingBookings: bookings.filter(b => b.status === 'pending').length,
        confirmedBookings: bookings.filter(b => b.status === 'confirmed').length,
        totalRevenue: bookings.reduce((sum, b) => sum + (b.total_amount || 0), 0)
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white shadow-sm border-b">
                <div className="max-w-7xl mx-auto px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <Typography.Title level={2} className="!mb-0">Booking Management</Typography.Title>
                            <Typography.Text type="secondary">Manage customer bookings and orders</Typography.Text>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto p-6">
                {error && <Alert type="error" message={error} className="mb-6" />}

                {/* Stats */}
                <Row gutter={[16, 16]} className="mb-6">
                    <Col xs={24} sm={6}>
                        <Card>
                            <Statistic
                                title="Total Bookings"
                                value={stats.totalBookings}
                                prefix={<ShoppingCartOutlined />}
                                valueStyle={{ color: '#1890ff' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={6}>
                        <Card>
                            <Statistic
                                title="Pending"
                                value={stats.pendingBookings}
                                prefix={<ClockCircleOutlined />}
                                valueStyle={{ color: '#fa8c16' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={6}>
                        <Card>
                            <Statistic
                                title="Confirmed"
                                value={stats.confirmedBookings}
                                prefix={<CheckCircleOutlined />}
                                valueStyle={{ color: '#52c41a' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={6}>
                        <Card>
                            <Statistic
                                title="Total Revenue"
                                value={stats.totalRevenue}
                                prefix="₹"
                                valueStyle={{ color: '#722ed1' }}
                                formatter={(value) => value.toLocaleString()}
                            />
                        </Card>
                    </Col>
                </Row>

                {/* Filters */}
                <Card className="mb-6">
                    <Row gutter={[16, 16]} align="middle">
                        <Col xs={24} sm={12} md={8}>
                            <Input
                                placeholder="Search bookings..."
                                prefix={<SearchOutlined />}
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                            />
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Select
                                placeholder="Filter by status"
                                value={statusFilter}
                                onChange={setStatusFilter}
                                style={{ width: '100%' }}
                                allowClear
                            >
                                <Select.Option value="pending">Pending</Select.Option>
                                <Select.Option value="confirmed">Confirmed</Select.Option>
                                <Select.Option value="preparing">Preparing</Select.Option>
                                <Select.Option value="ready">Ready</Select.Option>
                                <Select.Option value="delivered">Delivered</Select.Option>
                                <Select.Option value="cancelled">Cancelled</Select.Option>
                            </Select>
                        </Col>
                    </Row>
                </Card>

                {/* Bookings Table */}
                <Card>
                    <Table
                        columns={columns}
                        dataSource={filteredBookings}
                        rowKey="id"
                        size="middle"
                        loading={loading}
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} bookings`
                        }}
                        sticky
                        scroll={{ x: true, y: 520 }}
                        locale={{
                            emptyText: (
                                <Empty description="No bookings found" />
                            )
                        }}
                    />
                </Card>

                {/* Booking Details Modal */}
                <Modal
                    title="Booking Details"
                    open={modalVisible}
                    onCancel={() => setModalVisible(false)}
                    footer={[
                        <Button key="close" onClick={() => setModalVisible(false)}>
                            Close
                        </Button>
                    ]}
                    width={800}
                >
                    {viewingBooking && (
                        <div className="space-y-4">
                            <Descriptions bordered column={2}>
                                <Descriptions.Item label="Booking Number">
                                    <Typography.Text code>{viewingBooking.booking_number}</Typography.Text>
                                </Descriptions.Item>
                                <Descriptions.Item label="Status">
                                    <Tag color={getStatusColor(viewingBooking.status)}>
                                        {viewingBooking.status?.charAt(0).toUpperCase() + viewingBooking.status?.slice(1)}
                                    </Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label="Customer">
                                    {viewingBooking.user?.name}
                                </Descriptions.Item>
                                <Descriptions.Item label="Email">
                                    {viewingBooking.user?.email}
                                </Descriptions.Item>
                                <Descriptions.Item label="Vendor">
                                    {viewingBooking.vendor?.business_name}
                                </Descriptions.Item>
                                <Descriptions.Item label="Phone">
                                    {viewingBooking.contact_phone}
                                </Descriptions.Item>
                                <Descriptions.Item label="Event Date">
                                    {viewingBooking.event_date}
                                </Descriptions.Item>
                                <Descriptions.Item label="Event Time">
                                    {viewingBooking.event_time}
                                </Descriptions.Item>
                                <Descriptions.Item label="Guest Count">
                                    {viewingBooking.guest_count}
                                </Descriptions.Item>
                                <Descriptions.Item label="Total Amount">
                                    {formatCurrency(viewingBooking.total_amount)}
                                </Descriptions.Item>
                                <Descriptions.Item label="Delivery Address" span={2}>
                                    {viewingBooking.delivery_address}
                                </Descriptions.Item>
                                <Descriptions.Item label="Special Instructions" span={2}>
                                    {viewingBooking.special_instructions || 'None'}
                                </Descriptions.Item>
                            </Descriptions>
                        </div>
                    )}
                </Modal>
            </div>
        </div>
    );
}

export default AdminBookings;
